import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatBRL } from "@/lib/format";
import { getProductImageUrl } from "@/lib/utils";
import { Plus, Trash2, Edit3, LogOut, X, Package, ShoppingBag, Loader2, Eye, MapPin, User, Mail, Phone, FileText, Hash, Calendar, CreditCard } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  name: "", description: "", long_description: "", price: 0,
  image_url: "", category: "rastreador", stock: 0, sku: "",
  specs: "", active: true,
};

const Field = ({ label, name, value, onChange, type = "text", textarea = false, ...rest }) => (
  <label className="flex flex-col gap-2">
    <span className="text-xs font-quicksand font-bold text-[#3d3d3d]">{label}</span>
    {textarea ? (
      <textarea name={name} value={value} onChange={onChange} rows={3} data-testid={`form-${name}`} className="input-gp" {...rest} />
    ) : (
      <input type={type} name={name} value={value} onChange={onChange} data-testid={`form-${name}`} className="input-gp" {...rest} />
    )}
  </label>
);

export default function AdminDashboard() {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [trackingCode, setTrackingCode] = useState("");
  const [shippingOrder, setShippingOrder] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (viewingOrder) {
      setTrackingCode("");
    }
  }, [viewingOrder]);

  const handleShipOrder = async () => {
    if (!trackingCode.trim()) {
      toast.error("Por favor, digite o código de rastreio.");
      return;
    }
    setShippingOrder(true);
    try {
      const response = await api.post(`/admin/orders/${viewingOrder.id}/ship`, {
        tracking_code: trackingCode.trim()
      });
      toast.success("Pedido marcado como enviado! E-mail de rastreio disparado.");
      
      const updatedOrder = {
        ...viewingOrder,
        status: "SHIPPED",
        tracking_code: trackingCode.trim()
      };
      setViewingOrder(updatedOrder);
      setOrders((prev) => prev.map((o) => o.id === viewingOrder.id ? updatedOrder : o));
      setTrackingCode("");
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao atualizar envio.";
      toast.error(msg);
    } finally {
      setShippingOrder(false);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("Tem certeza de que deseja excluir este pedido? Esta ação não pode ser desfeita.")) {
      return;
    }
    try {
      await api.delete(`/admin/orders/${orderId}`);
      toast.success("Pedido excluído com sucesso.");
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
      if (viewingOrder?.id === orderId) {
        setViewingOrder(null);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao excluir pedido.";
      toast.error(msg);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("O tamanho do arquivo não deve ultrapassar 5MB.");
      e.target.value = "";
      return;
    }

    if (file.type !== "image/png" && file.type !== "image/jpeg" && file.type !== "image/jpg") {
      toast.error("Apenas imagens PNG ou JPG são permitidas.");
      e.target.value = "";
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const { data } = await api.post("/products/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, image_url: data.image_url }));
      toast.success("Imagem enviada com sucesso!");
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao enviar imagem.";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const hasToken = typeof window !== "undefined" && !!localStorage.getItem("gp_token");

  const logout = () => {
    localStorage.removeItem("gp_token");
    localStorage.removeItem("gp_admin_email");
    navigate("/admin/login");
  };

  const loadProducts = async () => {
    const { data } = await api.get("/products?include_inactive=true");
    setProducts(data);
  };
  const loadOrders = async () => {
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    }
  };

  useEffect(() => {
    if (!hasToken) return;
    Promise.all([loadProducts(), loadOrders()]).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [hasToken]);

  if (!hasToken) {
    return <Navigate to="/admin/login" replace />;
  }

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, description: p.description, long_description: p.long_description || "",
      price: p.price, image_url: p.image_url, category: p.category,
      stock: p.stock, sku: p.sku || "", specs: (p.specs || []).join("\n"), active: p.active,
    });
    setShowForm(true);
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        stock: parseInt(form.stock, 10) || 0,
        specs: form.specs.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Produto atualizado");
      } else {
        await api.post("/products", payload);
        toast.success("Produto criado");
      }
      setShowForm(false);
      loadProducts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Excluir este produto?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Produto excluído");
    loadProducts();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 py-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-quicksand font-bold text-[#1BB0CE] mb-2">
            PAINEL ADMINISTRATIVO
          </div>
          <h1 className="font-quicksand text-3xl font-extrabold text-[#3d3d3d]">Loja GoProtege</h1>
        </div>
        <button onClick={logout} data-testid="admin-logout-button" className="btn-gp-outline">
          <LogOut className="w-3.5 h-3.5" /> SAIR
        </button>
      </div>

      <div className="flex gap-2 mb-8 flex-wrap">
        <button
          onClick={() => setTab("products")}
          data-testid="tab-products"
          className={`inline-flex items-center gap-2 px-5 py-3 rounded-full font-quicksand text-sm font-bold transition-all ${
            tab === "products" ? "bg-gp-gradient text-white shadow-md" : "bg-white border border-[#e4e4e7] text-[#3d3d3d] hover:border-[#1BB0CE]"
          }`}
        >
          <Package className="w-4 h-4" /> Produtos ({products.length})
        </button>
        <button
          onClick={() => setTab("orders")}
          data-testid="tab-orders"
          className={`inline-flex items-center gap-2 px-5 py-3 rounded-full font-quicksand text-sm font-bold transition-all ${
            tab === "orders" ? "bg-gp-gradient text-white shadow-md" : "bg-white border border-[#e4e4e7] text-[#3d3d3d] hover:border-[#1BB0CE]"
          }`}
        >
          <ShoppingBag className="w-4 h-4" /> Pedidos ({orders.length})
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#1BB0CE]" />
        </div>
      ) : tab === "products" ? (
        <>
          <div className="flex justify-end mb-6">
            <button onClick={openCreate} data-testid="add-product-button" className="btn-gp">
              <Plus className="w-4 h-4" /> NOVO PRODUTO
            </button>
          </div>

          <div className="card-soft overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="bg-gp-gradient-light">
                <tr>
                  <th className="text-left px-5 py-4 text-xs font-quicksand font-bold text-[#3d3d3d] uppercase tracking-wider">Produto</th>
                  <th className="text-left px-5 py-4 text-xs font-quicksand font-bold text-[#3d3d3d] uppercase tracking-wider">Categoria</th>
                  <th className="text-right px-5 py-4 text-xs font-quicksand font-bold text-[#3d3d3d] uppercase tracking-wider">Preço</th>
                  <th className="text-right px-5 py-4 text-xs font-quicksand font-bold text-[#3d3d3d] uppercase tracking-wider">Estoque</th>
                  <th className="text-center px-5 py-4 text-xs font-quicksand font-bold text-[#3d3d3d] uppercase tracking-wider">Status</th>
                  <th className="text-right px-5 py-4 text-xs font-quicksand font-bold text-[#3d3d3d] uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#f1f1f3]">
                {products.map((p) => (
                  <tr key={p.id} data-testid={`product-row-${p.id}`} className="hover:bg-[#fafbfc]">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <img src={getProductImageUrl(p.image_url)} alt="" className="w-10 h-10 object-cover bg-gp-gradient-light rounded-lg" />
                        <span className="font-quicksand font-bold text-[#3d3d3d]">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-xs uppercase font-quicksand font-semibold text-[#666]">{p.category}</td>
                    <td className="px-5 py-3 text-right font-quicksand font-bold text-[#3d3d3d]">{formatBRL(p.price)}</td>
                    <td className="px-5 py-3 text-right font-quicksand text-[#666]">{p.stock}</td>
                    <td className="px-5 py-3 text-center">
                      <span className={`text-xs px-3 py-1 rounded-full font-quicksand font-bold uppercase ${p.active ? "bg-[#E6F9E8] text-[#0F7B3E]" : "bg-[#f4f4f5] text-[#666]"}`}>
                        {p.active ? "Ativo" : "Inativo"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => openEdit(p)} data-testid={`edit-${p.id}`} className="p-2 hover:bg-[#FAFAFA] text-[#1BB0CE] rounded-full">
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button onClick={() => deleteProduct(p.id)} data-testid={`delete-${p.id}`} className="p-2 hover:bg-[#FFF1F0] text-[#FF3B30] rounded-full">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="card-soft overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-gp-gradient-light">
              <tr>
                <th className="text-left px-5 py-4 text-xs font-quicksand font-bold text-[#3d3d3d] uppercase tracking-wider">Pedido</th>
                <th className="text-left px-5 py-4 text-xs font-quicksand font-bold text-[#3d3d3d] uppercase tracking-wider">Cliente</th>
                <th className="text-left px-5 py-4 text-xs font-quicksand font-bold text-[#3d3d3d] uppercase tracking-wider">Data</th>
                <th className="text-right px-5 py-4 text-xs font-quicksand font-bold text-[#3d3d3d] uppercase tracking-wider">Total</th>
                <th className="text-center px-5 py-4 text-xs font-quicksand font-bold text-[#3d3d3d] uppercase tracking-wider">Status</th>
                <th className="text-right px-5 py-4 text-xs font-quicksand font-bold text-[#3d3d3d] uppercase tracking-wider">Detalhes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f1f1f3]">
              {orders.length === 0 ? (
                <tr><td colSpan="6" className="px-5 py-12 text-center text-sm text-[#666]">Nenhum pedido ainda.</td></tr>
              ) : orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id}`} onClick={() => setViewingOrder(o)} className="hover:bg-[#fafbfc] cursor-pointer">
                  <td className="px-5 py-3 font-quicksand font-bold text-xs text-[#1BB0CE]">#{o.id.slice(0, 8).toUpperCase()}</td>
                  <td className="px-5 py-3">
                    <div className="font-quicksand font-bold text-[#3d3d3d]">{o.customer?.name}</div>
                    <div className="text-xs text-[#666]">{o.customer?.email}</div>
                  </td>
                  <td className="px-5 py-3 text-xs text-[#666]">{new Date(o.created_at).toLocaleString("pt-BR")}</td>
                  <td className="px-5 py-3 text-right font-quicksand font-bold text-[#3d3d3d]">{formatBRL(o.total)}</td>
                  <td className="px-5 py-3 text-center">
                    <span className={`text-xs px-3 py-1 rounded-full font-quicksand font-bold uppercase ${
                      o.status === "PAID" ? "bg-[#E6F9E8] text-[#0F7B3E]" :
                      o.status === "SHIPPED" ? "bg-[#E0F2FE] text-[#0369A1]" :
                      o.status === "PENDING" ? "bg-[#FFF6E5] text-[#B8740B]" :
                      "bg-[#f4f4f5] text-[#666]"
                    }`}>
                      {o.status === "PAID" ? "Pago" : o.status === "SHIPPED" ? "Enviado" : o.status === "PENDING" ? "Pendente" : o.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setViewingOrder(o)}
                        data-testid={`view-order-${o.id}`}
                        className="p-2 hover:bg-[#FAFAFA] text-[#1BB0CE] rounded-full"
                        aria-label="Ver detalhes"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteOrder(o.id)}
                        data-testid={`delete-order-${o.id}`}
                        className="p-2 hover:bg-red-50 text-red-500 rounded-full"
                        aria-label="Excluir pedido"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}



      {/* PRODUCT FORM MODAL */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl w-full max-w-2xl my-8 shadow-2xl" data-testid="product-form-modal">
            <div className="flex items-center justify-between p-6 border-b border-[#f1f1f3]">
              <h3 className="font-quicksand text-xl font-extrabold text-[#3d3d3d]">
                {editing ? "Editar produto" : "Novo produto"}
              </h3>
              <button onClick={() => setShowForm(false)} data-testid="close-form" className="p-2 hover:bg-[#f4f4f5] rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={saveProduct} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <Field label="Nome do produto" name="name" value={form.name} onChange={onChange} required />
              </div>
              <Field label="Categoria" name="category" value={form.category} onChange={onChange} placeholder="rastreador / rele / chicote / acessorio" />
              <Field label="SKU" name="sku" value={form.sku} onChange={onChange} />
              <Field label="Preço (R$)" name="price" value={form.price} onChange={onChange} type="number" step="0.01" required />
              <Field label="Estoque" name="stock" value={form.stock} onChange={onChange} type="number" />
              <div className="sm:col-span-2 flex flex-col gap-2">
                <Field label="URL da imagem" name="image_url" value={form.image_url} onChange={onChange} required />
                <div className="flex flex-col gap-1.5 mt-1">
                  <span className="text-xs font-quicksand font-bold text-[#666]">Ou faça o upload de uma imagem (PNG ou JPG, máx. 5MB)</span>
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      accept="image/png, image/jpeg, image/jpg"
                      onChange={handleImageUpload}
                      className="text-sm text-[#666] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-[#E6F7FA] file:text-[#1BB0CE] hover:file:bg-[#D0F0F5] cursor-pointer"
                      data-testid="image-upload-input"
                    />
                    {uploading && <span className="text-xs text-[#1BB0CE] animate-pulse">Enviando...</span>}
                  </div>
                </div>
              </div>
              <div className="sm:col-span-2">
                <Field label="Descrição curta" name="description" value={form.description} onChange={onChange} textarea />
              </div>
              <div className="sm:col-span-2">
                <Field label="Descrição detalhada" name="long_description" value={form.long_description} onChange={onChange} textarea />
              </div>
              <div className="sm:col-span-2">
                <Field label="Especificações (uma por linha)" name="specs" value={form.specs} onChange={onChange} textarea />
              </div>
              <label className="sm:col-span-2 flex items-center gap-2">
                <input type="checkbox" name="active" checked={form.active} onChange={onChange} data-testid="form-active" className="w-4 h-4 accent-[#1BB0CE]" />
                <span className="text-sm font-quicksand font-bold text-[#3d3d3d]">Produto ativo</span>
              </label>
              <div className="sm:col-span-2 flex gap-3 justify-end pt-4 border-t border-[#f1f1f3] mt-2">
                <button type="button" onClick={() => setShowForm(false)} className="btn-gp-outline">
                  CANCELAR
                </button>
                <button type="submit" data-testid="save-product-button" className="btn-gp">
                  {editing ? "SALVAR ALTERAÇÕES" : "CRIAR PRODUTO"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ORDER DETAILS MODAL */}
      {viewingOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setViewingOrder(null)}>
          <div className="bg-white rounded-3xl w-full max-w-3xl my-8 shadow-2xl" data-testid="order-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-[#f1f1f3]">
              <div>
                <div className="text-xs uppercase tracking-widest font-quicksand font-bold text-[#1BB0CE]">
                  PEDIDO #{viewingOrder.id.slice(0, 8).toUpperCase()}
                </div>
                <h3 className="font-quicksand text-2xl font-extrabold text-[#3d3d3d] mt-1">
                  Detalhes do pedido
                </h3>
              </div>
              <button onClick={() => setViewingOrder(null)} data-testid="close-order-detail" className="p-2 hover:bg-[#f4f4f5] rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
              {/* Status + Resumo */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-gp-gradient-light rounded-2xl p-4">
                  <div className="flex items-center gap-2 text-xs font-quicksand font-bold text-[#666] uppercase tracking-wider mb-2">
                    <Calendar className="w-3.5 h-3.5" /> Data
                  </div>
                  <div className="font-quicksand font-bold text-sm text-[#3d3d3d]">
                    {new Date(viewingOrder.created_at).toLocaleString("pt-BR")}
                  </div>
                </div>
                <div className="bg-gp-gradient-light rounded-2xl p-4">
                  <div className="flex items-center gap-2 text-xs font-quicksand font-bold text-[#666] uppercase tracking-wider mb-2">
                    <CreditCard className="w-3.5 h-3.5" /> Status
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full font-quicksand font-bold uppercase ${
                    viewingOrder.status === "PAID" ? "bg-[#E6F9E8] text-[#0F7B3E]" :
                    viewingOrder.status === "SHIPPED" ? "bg-[#E0F2FE] text-[#0369A1]" :
                    viewingOrder.status === "PENDING" ? "bg-[#FFF6E5] text-[#B8740B]" :
                    "bg-[#f4f4f5] text-[#666]"
                  }`}>
                    {viewingOrder.status === "PAID" ? "Pago" : viewingOrder.status === "SHIPPED" ? "Enviado" : viewingOrder.status === "PENDING" ? "Pendente" : viewingOrder.status}
                  </span>
                </div>
                <div className="bg-gp-gradient-light rounded-2xl p-4">
                  <div className="flex items-center gap-2 text-xs font-quicksand font-bold text-[#666] uppercase tracking-wider mb-2">
                    <Hash className="w-3.5 h-3.5" /> Total
                  </div>
                  <div className="font-quicksand text-xl font-extrabold text-gradient-gp">
                    {formatBRL(viewingOrder.total)}
                  </div>
                </div>
              </div>

              {/* Cliente */}
              <div className="card-soft p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-9 h-9 rounded-full bg-gp-gradient-light flex items-center justify-center">
                    <User className="w-4 h-4 text-[#1BB0CE]" />
                  </div>
                  <h4 className="font-quicksand text-base font-extrabold text-[#3d3d3d]">Dados do comprador</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <User className="w-4 h-4 text-[#999] mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[10px] font-quicksand font-bold uppercase text-[#999] tracking-wider">Nome</div>
                      <div className="font-quicksand font-bold text-[#3d3d3d]">{viewingOrder.customer?.name}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Mail className="w-4 h-4 text-[#999] mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[10px] font-quicksand font-bold uppercase text-[#999] tracking-wider">E-mail</div>
                      <div className="text-[#3d3d3d] break-all">{viewingOrder.customer?.email}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Phone className="w-4 h-4 text-[#999] mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[10px] font-quicksand font-bold uppercase text-[#999] tracking-wider">Telefone</div>
                      <div className="text-[#3d3d3d]">{viewingOrder.customer?.phone}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-[#999] mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[10px] font-quicksand font-bold uppercase text-[#999] tracking-wider">CPF/CNPJ</div>
                      <div className="text-[#3d3d3d]">{viewingOrder.customer?.cpf_cnpj}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Endereço */}
              <div className="card-soft p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-9 h-9 rounded-full bg-gp-gradient-light flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-[#1BB0CE]" />
                  </div>
                  <h4 className="font-quicksand text-base font-extrabold text-[#3d3d3d]">Endereço de entrega</h4>
                </div>
                <div className="text-sm text-[#3d3d3d] leading-relaxed">
                  <div className="font-quicksand font-bold">{viewingOrder.customer?.address}</div>
                  <div className="text-[#666] mt-1">
                    {viewingOrder.customer?.city} – {viewingOrder.customer?.state}
                  </div>
                  <div className="text-[#666]">CEP: {viewingOrder.customer?.zip_code}</div>
                </div>
              </div>

              {/* Itens */}
              <div className="card-soft p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-9 h-9 rounded-full bg-gp-gradient-light flex items-center justify-center">
                    <ShoppingBag className="w-4 h-4 text-[#1BB0CE]" />
                  </div>
                  <h4 className="font-quicksand text-base font-extrabold text-[#3d3d3d]">
                    Itens comprados ({viewingOrder.items?.length || 0})
                  </h4>
                </div>
                <ul className="divide-y divide-[#f1f1f3]">
                  {viewingOrder.items?.map((it, idx) => (
                    <li key={idx} className="py-3 flex gap-4 items-center" data-testid={`detail-item-${idx}`}>
                      <div className="w-14 h-14 bg-gp-gradient-light rounded-xl overflow-hidden flex-shrink-0">
                        <img src={getProductImageUrl(it.image_url)} alt={it.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-quicksand font-bold text-sm text-[#3d3d3d]">{it.name}</div>
                        <div className="text-xs text-[#666] mt-1">
                          Quantidade: <span className="font-quicksand font-bold text-[#3d3d3d]">{it.quantity}</span>
                          <span className="mx-2">·</span>
                          Unitário: <span className="font-quicksand font-bold text-[#3d3d3d]">{formatBRL(it.price)}</span>
                        </div>
                      </div>
                      <div className="font-quicksand text-sm font-extrabold text-[#3d3d3d]">
                        {formatBRL(it.price * it.quantity)}
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="border-t-2 border-[#f1f1f3] mt-3 pt-3 flex justify-between items-baseline">
                  <span className="text-xs uppercase tracking-wider font-quicksand font-bold text-[#666]">Total do pedido</span>
                  <span className="font-quicksand text-2xl font-extrabold text-gradient-gp">{formatBRL(viewingOrder.total)}</span>
                </div>
              </div>

              {/* Pagamento */}
              {viewingOrder.asaas_payment_id && (
                <div className="card-soft p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-full bg-gp-gradient-light flex items-center justify-center">
                      <CreditCard className="w-4 h-4 text-[#1BB0CE]" />
                    </div>
                    <h4 className="font-quicksand text-base font-extrabold text-[#3d3d3d]">Pagamento (Asaas)</h4>
                  </div>
                  <div className="text-xs text-[#666]">
                    <div>ID Asaas: <span className="font-quicksand font-bold text-[#3d3d3d]">{viewingOrder.asaas_payment_id}</span></div>
                    {viewingOrder.pix_expiration && (
                      <div className="mt-1">Expira em: <span className="font-quicksand font-bold text-[#3d3d3d]">{new Date(viewingOrder.pix_expiration).toLocaleString("pt-BR")}</span></div>
                    )}
                  </div>
                </div>
              )}

              {/* Rastreamento (Código de Rastreio) */}
              {viewingOrder.status === "PAID" && (
                <div className="card-soft p-5 border border-sky-100 bg-sky-50/30">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-full bg-gp-gradient-light flex items-center justify-center text-lg">
                      🚚
                    </div>
                    <h4 className="font-quicksand text-base font-extrabold text-[#3d3d3d]">Enviar Pedido (Loggi)</h4>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Código de rastreio (ex: LGI-ME26...)"
                      value={trackingCode}
                      onChange={(e) => setTrackingCode(e.target.value)}
                      className="flex-1 input-gp bg-white text-sm"
                    />
                    <button
                      onClick={handleShipOrder}
                      disabled={shippingOrder}
                      className="btn-gp px-5 py-2 font-quicksand font-bold text-xs shrink-0"
                    >
                      {shippingOrder ? "Salvando..." : "Salvar e Enviar"}
                    </button>
                  </div>
                </div>
              )}

              {viewingOrder.status === "SHIPPED" && (
                <div className="card-soft p-5 border border-green-100 bg-green-50/10">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-full bg-[#E6F9E8] flex items-center justify-center text-lg">
                      📦
                    </div>
                    <h4 className="font-quicksand text-base font-extrabold text-[#3d3d3d]">Rastreamento do Envio</h4>
                  </div>
                  <div className="text-xs text-[#666] font-quicksand">
                    <div>Status: <span className="font-bold text-[#0F7B3E] uppercase">Enviado</span></div>
                    <div className="mt-1">
                      Código: <span className="font-bold text-[#3d3d3d] monospace">{viewingOrder.tracking_code}</span>
                    </div>
                    <div className="mt-3">
                      <a
                        href={`https://www.melhorrastreio.com.br/app/loggi/${viewingOrder.tracking_code}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#1BB0CE] hover:underline font-bold text-xs inline-flex items-center gap-1"
                      >
                        Acompanhar no Melhor Rastreio ↗
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-5 border-t border-[#f1f1f3] flex justify-end">
              <button onClick={() => setViewingOrder(null)} className="btn-gp">
                FECHAR
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
